import { useState } from "react";
import { Formik } from "formik";
import {
  withIntl,
  intlShape,
  Column,
  Row,
  Form,
  TextInput,
  ButtonGroup,
  SubmitButton,
  CancelButton,
  CountriesSelectFormik,
  VatPrefixes,
  Heading,
} from "lcm-iot-commons";
import isEmail from "validator/lib/isEmail";
import TimeZoneTypeahead from "./TimeZoneTypeahead";
import LocaleTypeahead from "./LocaleTypeahead";
import { getLocaleFormat } from "../../utils/locales";

type OwnProps = {
  intl: intlShape;
  initialValues?: {
    id?: number;
    email?: string;
    firstName?: string;
    lastName?: string;
    ssoProvider?: string;
    locale?: {
      label?: string;
      identifier?: string;
    };
    address?: {
      company?: string;
      streetName?: string;
      streetNumber?: string;
      zipCode?: string;
      city?: string;
      regionCode?: string;
      country?: {
        name?: string;
        code?: string;
      };
      phone?: string;
      vatNumber?: string;
      billingEmail?: string;
    };
  };
  onSubmit?: $TSFixMeFunction;
  ssoUser?: boolean;
};

const defaultProps: Partial<OwnProps> = {
  initialValues: {
    address: {
      country: {
        code: undefined,
        name: undefined,
      },
    },
  },
  onSubmit: undefined,
  ssoUser: false,
};

type Props = OwnProps & typeof defaultProps;

export function UserContactDetailsForm({
  intl,
  initialValues,
  onSubmit,
  ssoUser,
}: Props) {
  const [localeFormat, setLocaleFormat] = useState(
    getLocaleFormat(initialValues?.locale),
  );
  const defaultLocaleFormat = getLocaleFormat("en-US");

  const validateAddressFields = (values: $TSFixMe, errors: $TSFixMe) => {
    const errorsForAddressFields = errors;
    const notAlphabetical = /^[^a-z]+$/i;
    if (!values.streetName) {
      errorsForAddressFields.streetName = intl.formatMessage({
        id: "validation.street_name.mandatory",
      });
    } else if (values.streetName?.length > 255) {
      errorsForAddressFields.streetName = intl.formatMessage(
        { id: "validation.street_name.too_long" },
        { max: "255" },
      );
    }
    if (!values.company) {
      errorsForAddressFields.company = intl.formatMessage({
        id: "validation.company.mandatory",
      });
    }
    if (!values.phone) {
      errorsForAddressFields.phone = intl.formatMessage({
        id: "validation.phone.mandatory",
      });
    } else if (
      values.phone?.length > 25 ||
      !notAlphabetical.test(values.phone)
    ) {
      errorsForAddressFields.phone = intl.formatMessage({
        id: "validation.phone.invalid",
      });
    }
    if (!values.zipCode) {
      errorsForAddressFields.zipCode = intl.formatMessage({
        id: "validation.zip_code.mandatory",
      });
    } else if (values.zipCode?.length > 20) {
      errorsForAddressFields.zipCode = intl.formatMessage(
        { id: "validation.zip_code.too_long" },
        { max: "20" },
      );
    }
    if (!values.city) {
      errorsForAddressFields.city = intl.formatMessage({
        id: "validation.city.mandatory",
      });
    } else if (values.city?.length > 255) {
      errorsForAddressFields.city = intl.formatMessage(
        { id: "validation.city.too_long" },
        { max: "255" },
      );
    }
    if (!values.country?.code) {
      errorsForAddressFields.country = intl.formatMessage({
        id: "validation.country.mandatory",
      });
    }
    if (values.company?.length > 255) {
      errorsForAddressFields.company = intl.formatMessage(
        { id: "validation.company.too_long" },
        { max: "255" },
      );
    }
    if (values.streetNumber?.length > 255) {
      errorsForAddressFields.streetNumber = intl.formatMessage(
        { id: "validation.street_number.too_long" },
        { max: "255" },
      );
    }
    if (values.regionCode?.length > 255) {
      errorsForAddressFields.regionCode = intl.formatMessage(
        { id: "validation.region_code.too_long" },
        { max: "255" },
      );
    }
    return errorsForAddressFields;
  };

  const validateForm = (values: $TSFixMe) => {
    const errors = {};
    if (!values.firstName) {
      (errors as $TSFixMe).firstName = intl.formatMessage({
        id: "validation.first_name.mandatory",
      });
    } else if (values.firstName.length > 255) {
      (errors as $TSFixMe).firstName = intl.formatMessage(
        { id: "validation.first_name.too_long" },
        { max: "255" },
      );
    }
    if (!values.lastName) {
      (errors as $TSFixMe).lastName = intl.formatMessage({
        id: "validation.last_name.mandatory",
      });
    } else if (values.lastName.length > 255) {
      (errors as $TSFixMe).lastName = intl.formatMessage(
        { id: "validation.last_name.too_long" },
        { max: "255" },
      );
    }
    if (values.billingEmail && !isEmail(values.billingEmail)) {
      (errors as $TSFixMe).billingEmail = intl.formatMessage({
        id: "validation.email.invalid",
      });
    }
    return validateAddressFields(values, errors);
  };

  const renderForm = (formProps: $TSFixMe) => {
    const { isSubmitting, values } = formProps;
    const vatPrefix = (VatPrefixes as $TSFixMe).forCountryCode(
      values?.country?.code,
    );
    if (values?.country?.code && !vatPrefix) {
      values.vatNumber = "";
    }
    return (
      <Form {...formProps}>
        <Row id="contact-details-company">
          <Column md={8}>
            <TextInput
              {...formProps}
              id="company"
              name="company"
              label={intl.formatMessage({ id: "label.company" })}
              required
            />
          </Column>
        </Row>
        <Row id="contact-details-name">
          <Column md={4}>
            <TextInput
              {...formProps}
              id="first-name"
              name="firstName"
              label={intl.formatMessage({ id: "label.first_name" })}
              disabled={ssoUser}
              required
            />
          </Column>
          <Column md={4}>
            <TextInput
              {...formProps}
              id="last-name"
              name="lastName"
              label={intl.formatMessage({ id: "label.last_name" })}
              disabled={ssoUser}
              required
            />
          </Column>
        </Row>
        <Row id="contact-details-street">
          <Column md={6}>
            <TextInput
              {...formProps}
              id="street-name"
              name="streetName"
              label={intl.formatMessage({ id: "label.street_name" })}
              required
            />
          </Column>
          <Column md={2}>
            <TextInput
              {...formProps}
              id="street-number"
              name="streetNumber"
              label={intl.formatMessage({ id: "label.street_number" })}
            />
          </Column>
        </Row>
        <Row id="contact-details-city">
          <Column md={2}>
            <TextInput
              {...formProps}
              id="zip-code"
              name="zipCode"
              label={intl.formatMessage({ id: "label.zip_code" })}
              required
            />
          </Column>
          <Column md={6}>
            <TextInput
              {...formProps}
              id="city"
              name="city"
              label={intl.formatMessage({ id: "label.city" })}
              required
            />
          </Column>
        </Row>
        <Row id="contact-details-country">
          <Column md={2}>
            <TextInput
              {...formProps}
              id="region-code"
              name="regionCode"
              label={intl.formatMessage({ id: "label.region_code" })}
            />
          </Column>
          <Column md={6}>
            <CountriesSelectFormik
              {...formProps}
              id="country"
              name="country"
              label={intl.formatMessage({ id: "label.country" })}
              required
              all
            />
          </Column>
        </Row>
        <Row id="contact-details-phone">
          <Column md={8}>
            <TextInput
              {...formProps}
              id="phone"
              name="phone"
              label={intl.formatMessage({ id: "label.phone" })}
              required
            />
          </Column>
        </Row>
        <Row id="contact-details-time-zone">
          <Column md={8}>
            <TimeZoneTypeahead
              {...formProps}
              data-testid="user-time-zone"
              name="timeZone"
              label={intl.formatMessage({ id: "label.time_zone" })}
            />
          </Column>
        </Row>
        <Row id="contact-details-locale" className="hidden">
          <Column md={3}>
            <LocaleTypeahead
              {...formProps}
              data-testid="user-locale"
              name="locale"
              label={intl.formatMessage({ id: "label.locale" })}
              onChange={(locale: $TSFixMe) => {
                formProps.setFieldValue("locale", locale);
                setLocaleFormat(getLocaleFormat(locale.identifier));
              }}
            />
          </Column>
          <Column md={5}>
            <div className="form-group" id="locale-format">
              <p>{intl.formatMessage({ id: "label.locale_format" })}</p>
              <p className="disabled strong" data-testid="locale-format">
                {localeFormat || defaultLocaleFormat}
              </p>
            </div>
          </Column>
        </Row>
        <Heading
          level={3}
          id="user-profile-edit-contact-details-header"
          title={intl.formatMessage({
            id: "subscription_details.billing_details_header",
          })}
        />
        <Row id="user-profile-billing-email">
          <Column md={8}>
            <TextInput
              {...formProps}
              id="billing-email"
              component={TextInput}
              label={intl.formatMessage({ id: "label.email" })}
              name="billingEmail"
              disabled={isSubmitting}
              help
              helpTitle={intl.formatMessage({ id: "label.email" })}
              helpText={intl.formatMessage({
                id: "edit_contact_details.billing_details_email.description",
              })}
            />
          </Column>
        </Row>
        {vatPrefix && (
          <Row id="contact-details-vat">
            <Column md={8}>
              <TextInput
                {...formProps}
                id="vat_number"
                prefix={vatPrefix}
                name="vatNumber"
                label={intl.formatMessage({ id: "label.vat_number" })}
              />
            </Column>
          </Row>
        )}
        {/* @ts-expect-error TS(2322) FIXME: Type '{ children: Element[]; className: string; }'... Remove this comment to see the full error message */}
        <ButtonGroup className="btn-group">
          <SubmitButton
            id="contact-details-submit"
            fetching={isSubmitting}
            disabled={!formProps.dirty}
          />
          <CancelButton
            id="contact-details-cancel"
            // @ts-expect-error TS(2322) FIXME: Type '{ id: string; disabled: any; intl: IntlShape... Remove this comment to see the full error message
            disabled={isSubmitting}
            intl={intl}
            target="/profile"
          />
        </ButtonGroup>
      </Form>
    );
  };

  return (
    <div className="form-group">
      <Formik
        id="user-contact-details-form"
        initialValues={initialValues}
        // @ts-expect-error TS(2322) FIXME: Type '$TSFixMeFunction | undefined' is not assigna... Remove this comment to see the full error message
        onSubmit={onSubmit}
        validate={validateForm}
      >
        {renderForm}
      </Formik>
    </div>
  );
}

UserContactDetailsForm.defaultProps = defaultProps;

export default withIntl(UserContactDetailsForm);
